import React, { useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  CellLeftV8,
  order2Sorting,
  toastSuccess,
  toastFailure,
  promiseModalDialog,
  TableDataState,
  EditCellV8,
  TableCellV8,
  TablePaginatedV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import * as Lo from 'lodash';
import { getRouteByName } from 'app/routes';
/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateMaterial] = useMutation(gqlUpdate);
  const [addNewMaterial] = useMutation(gqlAdd);
  const [deleteMaterial] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('User'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellLeftV8,
        size: 5,
      },
      {
        id: 'userName',
        header: t('Χρήστης'),
        columnName: t('Χρήστης'),
        accessorKey: 'userName',
        orderByField: 'userName',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.userName}
              {!row.original.hasUserPassword && (
                <div style={{ color: 'red' }}>({t('Δεν έχει κωδικό')})</div>
              )}
            </div>
          );
        },
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'firstName',
        header: t('Όνομα'),
        columnName: t('Όνομα'),
        accessorKey: 'firstName',
        orderByField: 'firstName',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'lastName',
        header: t('Επώνυμο'),
        columnName: t('Επώνυμο'),
        accessorKey: 'lastName',
        orderByField: 'lastName',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'email',
        header: t('E-mail'),
        columnName: t('E-mail'),
        accessorKey: 'email',
        orderByField: 'email',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'userType.name',
        header: t('Τύπος Χρήστη'),
        columnName: t('Τύπος Χρήστη'),
        accessorKey: 'userType.name',
        orderByField: 'userType.name',
        cell: ({ row }) => {
          return (
            <div>
              {row.original.userType?.name}
              <div style={{ color: 'gray' }}>
                {row.original.userview?.referenceName}
                {row.original.userview?.referenceName2 && (
                  <div>{row.original.userview?.referenceName2}</div>
                )}
              </div>
            </div>
          );
        },
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'position',
        header: t('Θέση'),
        columnName: t('Θέση'),
        accessorKey: 'position',
        orderByField: 'position',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'mobile1',
        header: t('Κινητό'),
        columnName: t('Κινητό'),
        accessorKey: 'mobile1',
        orderByField: 'mobile1',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      isActiveColumn(t, i18n),
    ],
    [t],
  );

  const tableRef = useRef(null);

  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
